import { BigDataEventConstants } from "./constants/big-data-constants";
import { Selector } from "./constants/closing-without-lp";
import { EventType } from "./constants/event-type-constants";
import { BigDataHelper } from "./helpers/big-data-helper";
import { DOMHelper } from "./helpers/dom-helper";
import { BigData } from "./marketing-client-bigdata";
import { ClosingWithoutLp } from "./marketing-client-closing-without-lp";
import { LiveSession } from "./utils/livesession";

export class HardClosing extends ClosingWithoutLp {
    section: HTMLElement;
    header: HTMLElement;
    desktopScrollButton: HTMLElement;
    mobileScrollButton: HTMLElement;
    mobileActionButton: HTMLElement;
    mobileFixedActionButton: HTMLElement;

    init() {
        this.initializeHardClosing();
        this.delayLoading();
        this.prepareAccordionHeaders(Selector.hardClosingAccordionHeader);
        this.toggleAccordionEvents(Selector.hardClosingAccordionArrow);
        if (this.isMobile) {
            this.initializeMobileScrollButton();
            this.initializeMobileActionButton();
            this.initializeMobileFixedButton();
            this.bindScrollOnClick(this.mobileScrollButton);
            this.bindScrollButtonInViewportEvent(this.mobileScrollButton);
        } else {
            this.initializeDesktopScrollButton();
            this.bindScrollOnClick(this.desktopScrollButton);
            this.bindScrollButtonInViewportEvent(this.desktopScrollButton);
        }
    }

    initializeHardClosing() {
        this.initializeSection();
        this.moveHardClosingOnTheScreen();
        this.initializeHeader();
        this.addDefaultClasses();
    }

    delayLoading() {
        const delayTime: number = this.section?.dataset.loadingTime ? +this.section.dataset.loadingTime : 1;

        setTimeout(() => {
            this.section?.classList.remove(Selector.hidden);
            this.initializeLiveSession();
            this.sendEvent();
        }, delayTime * 1000);
    }

    moveHardClosingOnTheScreen() {
        const element = DOMHelper.querySelector(Selector.placeholder) as HTMLElement;

        if (element) {
            const contentHeight = this.isMobile ? 440 : 370;
            const elDistanceToViewport = window.innerHeight - element.offsetTop;

            const pixelsToMove = -Math.abs(contentHeight - elDistanceToViewport);
            const marginValue = pixelsToMove.toString() + "px";
            this.section.style.marginTop = marginValue;
        }
    }

    initializeSection() {
        this.section = DOMHelper.querySelector(Selector.hardClosing) as HTMLElement;
    }

    initializeHeader() {
        this.header = DOMHelper.querySelector(Selector.hardClosingHeader) as HTMLElement;
    }

    initializeDesktopScrollButton() {
        this.desktopScrollButton = DOMHelper.querySelector(Selector.hardClosingDesktopScrollButton, this.section) as HTMLElement;
    }

    initializeMobileActionButton() {
        this.mobileActionButton = DOMHelper.querySelector(Selector.hardClosingMobileActionButton, this.section) as HTMLElement;
    }

    initializeMobileFixedButton() {
        this.mobileFixedActionButton = DOMHelper.querySelector(Selector.hardClosingMobileFixedButton) as HTMLElement;
    }

    initializeMobileScrollButton() {
        this.mobileScrollButton = DOMHelper.querySelector(Selector.hardClosingMobileScrollButton, this.section) as HTMLElement;
    }

    initializeLiveSession() {
        if (!this.section) return;

        new LiveSession(this.section).init();
    }

    addDefaultClasses() {
        this.section?.classList.add(Selector.closer);
        this.header?.classList.add(Selector.sticky);
    }

    bindScrollOnClick(element: HTMLElement) {
        element?.addEventListener(EventType.CLICK, () => {
            const elementToScrollTo = DOMHelper.querySelector(Selector.hardClosingConditionsOfPromotion, this.section) as HTMLElement;
            this.scrollToElement(elementToScrollTo);
        });
    }

    bindScrollButtonInViewportEvent(element: HTMLElement) {
        const observer = new IntersectionObserver((entries, observer) => {
            entries[0].isIntersecting ? this.hideScrollButton(element) : this.showScrollButton(element);
            if (this.isMobile) entries[0].isIntersecting || entries[0].boundingClientRect.y < 0 ? this.showFixedButton() : this.showInlineButton();
        });

        const elementToObserve = DOMHelper.querySelector(Selector.hardClosingAccordionsContent, this.section);
        if (elementToObserve) observer.observe(elementToObserve as Element);
    }

    hideScrollButton(element: HTMLElement) {
        element?.classList.add(Selector.unvisible);
    }

    showScrollButton(element: HTMLElement) {
        element?.classList.remove(Selector.unvisible);
    }

    showFixedButton() {
        this.mobileActionButton?.classList.add(Selector.hidden);
        this.mobileFixedActionButton?.classList.remove(Selector.hidden)
    }

    showInlineButton() {
        this.mobileActionButton?.classList.remove(Selector.hidden);
        this.mobileFixedActionButton?.classList.add(Selector.hidden);
    }

    scrollToElement(element: HTMLElement) {
        element?.scrollIntoView(false);
    }

    sendEvent() {
        if (this.section) {
            const { cta, ctaCategory } = BigDataHelper.createAttribute(this.section, true),
            { adaptivePaywallRuleId } = window[Selector.marketingParams];

            const toSend = {
                cta: cta,
                ctaCategory: ctaCategory,
                ruleId: adaptivePaywallRuleId,
            }
            BigData.sendDataOceanEvent(BigDataEventConstants.onLoadingHardClosing, toSend);
        }
    }
}
